import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';

const Icons = {
  Building: () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  ),
  Download: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  )
};

const HospitalEarnings = () => {
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalHospitals, setTotalHospitals] = useState(0);

  useEffect(() => {
    fetchHospitalEarnings();
  }, []);

  const fetchHospitalEarnings = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://anesthetist-tracker-api.kaushalvaja3.workers.dev/api';
      const token = localStorage.getItem('token');

      // Fetch services to calculate earnings per hospital
      const servicesResp = await fetch(`${API_URL}/services`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (servicesResp.ok) {
        const services = await servicesResp.json();
        
        // Group services by hospital
        const hospitalMap = {};
        services.forEach(service => {
          const hospitalName = service.hospital_name || 'Unknown';
          if (!hospitalMap[hospitalName]) {
            hospitalMap[hospitalName] = {
              name: hospitalName,
              totalEarnings: 0,
              totalDays: 0,
              serviceCount: 0
            };
          }
          hospitalMap[hospitalName].totalEarnings += parseFloat(service.amount_charged || 0);
          hospitalMap[hospitalName].totalDays += parseInt(service.days_of_service || 0);
          hospitalMap[hospitalName].serviceCount += 1;
        });

        // Convert to array and calculate avg per day
        const hospitalArray = Object.values(hospitalMap).map(h => ({
          ...h,
          avgPerDay: h.totalDays > 0 ? h.totalEarnings / h.totalDays : 0
        })).sort((a, b) => b.totalEarnings - a.totalEarnings);

        setHospitals(hospitalArray);
        setTotalHospitals(hospitalArray.length);
      }
    } catch (error) {
      console.error('Error fetching hospital earnings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    // Create CSV content
    const headers = ['Hospital', 'Total Earnings', 'Total Days', 'Services', 'Avg Per Day'];
    const rows = hospitals.map(h => [
      h.name,
      `$${h.totalEarnings.toFixed(2)}`,
      h.totalDays,
      h.serviceCount,
      `$${h.avgPerDay.toFixed(2)}`
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hospital-earnings-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
              <Link to="/dashboard" className="hover:text-blue-600">Dashboard</Link>
              <span>›</span>
              <span className="text-gray-900 font-medium">Hospital Earnings</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Hospital Earnings & Service Days</h1>
            <p className="text-sm text-gray-500 mt-1">Total: {totalHospitals} hospitals</p>
          </div>
          <button
            onClick={handleExport}
            className="bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors font-medium text-sm"
          >
            <Icons.Download />
            Export Data
          </button>
        </div>

        {/* Hospital Earnings Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hospital
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Earnings
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Days
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Services
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Avg Per Day
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {hospitals.length > 0 ? (
                  hospitals.map((hospital, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="bg-blue-100 rounded-lg p-2">
                            <div className="text-blue-600">
                              <Icons.Building />
                            </div>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{hospital.name}</p>
                            <p className="text-xs text-gray-500">ID: {index + 1}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-lg font-bold text-green-600">
                          ${hospital.totalEarnings.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-900">{hospital.totalDays} days</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {hospital.serviceCount} services
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-semibold text-gray-900">
                          ${hospital.avgPerDay.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-400">
                      <p>No hospital earnings data available</p>
                      <Link to="/services/new" className="text-blue-600 hover:text-blue-700 font-medium text-sm mt-2 inline-block">
                        Add your first service →
                      </Link>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default HospitalEarnings;
